import type DataSourceId from "../state/DataSourceId";
import type { GenericSchema } from "valibot";
import type { IconName } from "../components/icon/Icon";
import type { JSX } from "solid-js";
import type { FormApi } from "../components/form/createForm";
import type { TranslationKey } from "../state/i18n";
import type { DataSourceDto } from "../services/ApiService";
import { keyFromSelector } from "i18next";

export type DataSourceType = "EXCEL" | "URL" | "GOOGLE_SPREADSHEET";
export type DataSourceUpdateStatus = "SUCCESS" | "FAILURE" | "NEVER";
export type DataSourceSearchType = "LIVE" | "LOCAL_FALLBACK";

export type DataSource = {
  id: DataSourceId;
  name: string;
  type: DataSourceType;
  enabled: boolean;
  lastUpdatedAt?: Date;
  updateStatus: DataSourceUpdateStatus;
  lastUpdateWarningMessages: string[];
  columnMapping: { id: string; label: string }[];
  searchType: DataSourceSearchType;
  failedExecutionCount: number;
  configuration: Record<string, unknown>;
};

export type DataSourceDefinition<T, DTO> = {
  name: TranslationKey;
  type: DataSourceType;
  icon: IconName;
  schema: GenericSchema<T>;
  initialValue: Partial<T>;
  onCreated?: (dataSource: DataSource, input: T) => void;
  toDto: (input: T) => DTO;
  toConfiguration: (input: DataSourceDto) => T;
  extraAction?: (props: { dataSource: DataSource }) => JSX.Element;
  EditItem: (input: {
    type: "CREATE" | "EDIT";
    value: FormApi<T>["getValue"];
    updateValue: FormApi<T>["updateValue"];
    error: FormApi<T>["getError"];
  }) => JSX.Element;
};

export const UPDATE_INTERVALS = ["QUARTER_HOUR", "HALF_HOUR", "HOURLY", "DAILY", "WEEKLY", "NEVER"];
export type UpdateInterval = (typeof UPDATE_INTERVALS)[number];
export const UPDATE_INTERVAL_OPTIONS: { value: UpdateInterval; label: TranslationKey }[] = [
  { value: "NEVER", label: keyFromSelector(($) => $.DATASOURCE_UPDATE_INTERVAL_NEVER) },
  { value: "QUARTER_HOUR", label: keyFromSelector(($) => $.DATASOURCE_UPDATE_INTERVAL_QUARTER_HOUR) },
  { value: "HALF_HOUR", label: keyFromSelector(($) => $.DATASOURCE_UPDATE_INTERVAL_HALF_HOUR) },
  { value: "HOURLY", label: keyFromSelector(($) => $.DATASOURCE_UPDATE_INTERVAL_HOURLY) },
  { value: "DAILY", label: keyFromSelector(($) => $.DATASOURCE_UPDATE_INTERVAL_DAILY) },
  { value: "WEEKLY", label: keyFromSelector(($) => $.DATASOURCE_UPDATE_INTERVAL_WEEKLY) },
];
export const resolveUpdateInterval = (input: string): UpdateInterval =>
  UPDATE_INTERVALS.find((value) => value === input) ?? "NEVER";
